import type { NextActionKey } from './record'

/**
 * The hand-over's routes, by the screen behind each. The only place in the dashboard a hand-over
 * route is written out: every link into the flow, every landing out of a step and every route
 * file reads this table (`route-literals.test.ts` is what keeps it so), so the eighth screen is
 * a row here and nowhere else.
 */
export const HANDOVER_ROUTES = {
  licence: '/bookings/$bookingId/licence',
  contact: '/bookings/$bookingId/contact',
  drive: '/bookings/$bookingId/drive',
  condition: '/bookings/$bookingId/condition',
  agreement: '/bookings/$bookingId/agreement',
  active: '/bookings/$bookingId/active',
  return: '/bookings/$bookingId/return',
} as const

export type HandoverScreen = keyof typeof HANDOVER_ROUTES
export type HandoverRoute = (typeof HANDOVER_ROUTES)[HandoverScreen]

/**
 * The walk-in's own routes, keyed by the only id it has.
 *
 * A drive started from a tile writes no `bookings` row (ADR 0005), so there is no `$bookingId` to
 * address its screens by and they live under `/drives/{id}/` instead. Kept as a table of its own
 * rather than a seventh row above, because the two take different parameters and a reader that
 * could hold either would have to be handed both.
 *
 * Three screens: the agreement, which is the only thing between a walk-in and the keys, and the
 * drive in progress and its return desk, without which a walk-in that went out could never come
 * back (`uq_activities_active_vehicle` would hold the car for ever). Each is the booking's own
 * screen, addressed by the drive's id; nothing else of the hand-over has a walk-in version,
 * because a walk-in has no record to hang it off.
 */
export const DRIVE_ROUTES = {
  agreement: '/drives/$driveId/agreement',
  active: '/drives/$driveId/active',
  return: '/drives/$driveId/return',
} as const

export type DriveRoute = (typeof DRIVE_ROUTES)[keyof typeof DRIVE_ROUTES]

/** The booking's record, which every step of the flow hangs off. */
export const RECORD_ROUTE = '/bookings/$bookingId'

/** Where a `Continue` can go: a step of the hand-over, or the record it belongs to. */
export type LandingRoute = HandoverRoute | typeof RECORD_ROUTE

/**
 * Where the primary goes when read from the record's card: a route, the day itself, or the
 * phone — the one verb with no screen behind it, which the card draws as a `tel:` link.
 */
export type Landing = { kind: 'route'; to: LandingRoute | '/' } | { kind: 'call' }

/**
 * Who is reading the answer.
 *
 * The record's `card` is read by somebody who opened the record to act on it: the car is out, so
 * the primary opens the return desk, and a finished drive offers the day. A `continue` is tapped
 * by somebody who has just finished a step: the car is out, so what there is to look at is the
 * drive in progress, and the return desk is one tap on from it; and a finished drive has no
 * `Back to today` for a flow screen to offer — the record says there is nothing left, which is
 * the right place to be told.
 */
export type Reader = 'card' | 'continue'

type Row = { card: Landing; continue: LandingRoute }

const route = (to: LandingRoute | '/'): Landing => ({ kind: 'route', to })

/**
 * One table, both readers. The difference between them is two columns of one row each rather
 * than a second table, so a ninth key is a ninth row that the compiler asks for, and cannot be
 * added to one reader and forgotten by the other.
 */
const LANDINGS: Record<NextActionKey, Row> = {
  today: { card: route('/'), continue: RECORD_ROUTE },
  inspect: { card: route(HANDOVER_ROUTES.condition), continue: HANDOVER_ROUTES.condition },
  call: { card: { kind: 'call' }, continue: HANDOVER_ROUTES.active },
  return: { card: route(HANDOVER_ROUTES.return), continue: HANDOVER_ROUTES.active },
  photos: { card: route(HANDOVER_ROUTES.condition), continue: HANDOVER_ROUTES.condition },
  agreement: { card: route(HANDOVER_ROUTES.agreement), continue: HANDOVER_ROUTES.agreement },
  handover: { card: route(HANDOVER_ROUTES.drive), continue: HANDOVER_ROUTES.drive },
  licence: { card: route(HANDOVER_ROUTES.licence), continue: HANDOVER_ROUTES.licence },
}

/** Where `key` goes for whoever is asking. A `continue` always has a route to go to. */
export function landing(key: NextActionKey, reader: 'continue'): LandingRoute
export function landing(key: NextActionKey, reader: 'card'): Landing
export function landing(key: NextActionKey, reader: Reader): Landing | LandingRoute {
  return reader === 'card' ? LANDINGS[key].card : LANDINGS[key].continue
}
